import { TableColumnDictionary } from '@/lib/types/Table';
import { TableStats } from '@/lib/types/TableStats';

function hasKeys<T>(object: T) {
  return !!Object.keys(object ?? {}).length;
}

export const FALLBACK_VALUES = {
  missing: null,
  distinct: null,
  unique: null,
  minimum: null,
  maximum: null,
  mean: null,
  median: null,
  stdev: null,
};

interface CombineColumnStatisticsParams {
  tableColumns: TableColumnDictionary;
  tableStatistics?: TableStats | null;
  descriptionFormat?: 'markdown' | 'plaintext';
}

export const combineColumnStatistics = ({
  tableColumns,
  tableStatistics,
  descriptionFormat = 'plaintext',
}: CombineColumnStatisticsParams) => {
  const canCalculate =
    !!tableStatistics &&
    hasKeys(tableStatistics) &&
    hasKeys(tableStatistics.column_statistics);

  if (!canCalculate) {
    return Array.from(
      Object.values(tableColumns),
      ({ name, datatype, description, title }) => ({
        name,
        datatype,
        title: title.en,
        description: description.markdown.en,
        ...FALLBACK_VALUES,
      }),
    );
  }

  const data = Array.from(
    Object.entries(tableColumns),
    ([columnName, column]) => {
      const columnStatistics =
        tableStatistics.column_statistics?.[columnName] ?? FALLBACK_VALUES;

      const { name, datatype, title } = column;
      const description = column.description[descriptionFormat].en;

      // TODO: remove this abstraction and just combine
      return {
        name,
        datatype,
        description,
        title: title.en,
        length_histogram: columnStatistics.length_histogram,
        histogram: columnStatistics.histogram,
        missing: columnStatistics.null_percent,
        distinct: columnStatistics.distinct_count,
        unique: columnStatistics.unique_percent,
        minimum:
          columnStatistics.minimum_value ?? columnStatistics.minimum_length,
        maximum:
          columnStatistics.maximum_value ?? columnStatistics.maximum_length,
        mean: columnStatistics.average ?? columnStatistics.average_length,
        median: columnStatistics.median ?? columnStatistics.median_length,
        stdev:
          columnStatistics.standard_deviation ??
          columnStatistics.standard_deviation_of_length,
      };
    },
  );

  return data;
};
